// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import CheckIn from "./check-in.js"
import SurveyStep from "./survey-step.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": true
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "survey_steps_count": {
      "column": {
        "default": "0",
        "name": "survey_steps_count",
        "null": false,
        "type": "integer"
      },
      "name": "survey_steps_count",
      "selected_by_default": null,
      "translated": false
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": true
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": false,
      "translated": true
    },
    "name_da": {
      "column": null,
      "name": "name_da",
      "selected_by_default": false,
      "translated": false
    },
    "name_en": {
      "column": null,
      "name": "name_en",
      "selected_by_default": false,
      "translated": false
    },
    "name_no": {
      "column": null,
      "name": "name_no",
      "selected_by_default": false,
      "translated": false
    },
    "name_se": {
      "column": null,
      "name": "name_se",
      "selected_by_default": false,
      "translated": false
    }
  },
  "className": "Survey",
  "collectionKey": "surveys",
  "collectionName": "surveys",
  "i18nKey": "survey",
  "camelizedLower": "survey",
  "name": "Survey",
  "nameDasherized": "survey",
  "pluralName": "surveys",
  "ransackable_associations": [
    {
      "className": "Survey::Audit",
      "collectionName": "survey_audits",
      "foreignKey": "survey_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "SurveyAudit",
      "through": null
    },
    {
      "className": "CheckIn",
      "collectionName": "check_ins",
      "foreignKey": "survey_id",
      "name": "check_ins",
      "macro": "has_many",
      "resource_name": "CheckIn",
      "through": null
    },
    {
      "className": "CourseModule",
      "collectionName": "course_modules",
      "foreignKey": "survey_id",
      "name": "course_modules",
      "macro": "has_many",
      "resource_name": "CourseModule",
      "through": null
    },
    {
      "className": "Survey::Audit",
      "collectionName": "survey_audits",
      "foreignKey": "survey_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "SurveyAudit",
      "through": null
    },
    {
      "className": "Survey::Translation",
      "collectionName": null,
      "foreignKey": "survey_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "SurveyStepElement",
      "collectionName": "survey_step_elements",
      "foreignKey": "survey_step_id",
      "name": "elements",
      "macro": "has_many",
      "resource_name": "SurveyStepElement",
      "through": "steps"
    },
    {
      "className": "SurveyStep",
      "collectionName": "survey_steps",
      "foreignKey": "survey_id",
      "name": "steps",
      "macro": "has_many",
      "resource_name": "SurveyStep",
      "through": null
    },
    {
      "className": "TeamSurvey",
      "collectionName": "team_surveys",
      "foreignKey": "survey_id",
      "name": "team_surveys",
      "macro": "has_many",
      "resource_name": "TeamSurvey",
      "through": null
    },
    {
      "className": "Survey::Translation",
      "collectionName": null,
      "foreignKey": "survey_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "survey_steps_count",
      "column": {
        "default": "0",
        "name": "survey_steps_count",
        "null": false,
        "type": "integer"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "CheckIn",
      "collectionName": "check_ins",
      "foreignKey": "survey_id",
      "name": "check_ins",
      "macro": "has_many",
      "resource_name": "CheckIn",
      "through": null
    },
    {
      "className": "SurveyStep",
      "collectionName": "survey_steps",
      "foreignKey": "survey_id",
      "name": "steps",
      "macro": "has_many",
      "resource_name": "SurveyStep",
      "through": null
    }
  ],
  "paramKey": "survey",
  "primaryKey": "id"
}

/** Frontend model for Survey. */
export default class Survey extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {number} */
  surveyStepsCount() {
    return this.readAttributeUnderscore("survey_steps_count")
  }

  /** @returns {boolean} */
  hasSurveyStepsCount() {
    const value = this.surveyStepsCount()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameDa() {
    return this.readAttributeUnderscore("name_da")
  }

  /** @returns {boolean} */
  hasNameDa() {
    const value = this.nameDa()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameEn() {
    return this.readAttributeUnderscore("name_en")
  }

  /** @returns {boolean} */
  hasNameEn() {
    const value = this.nameEn()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameNo() {
    return this.readAttributeUnderscore("name_no")
  }

  /** @returns {boolean} */
  hasNameNo() {
    const value = this.nameNo()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameSe() {
    return this.readAttributeUnderscore("name_se")
  }

  /** @returns {boolean} */
  hasNameSe() {
    const value = this.nameSe()

    return this._isPresent(value)
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  clone(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "clone",
        primaryKey: this.primaryKey(),
        collectionName: "surveys",
        type: "member"
      },
      commandArgs
    ))
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./check-in.js").default>} */
  checkIns() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["survey_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "check_ins",
        model: this,
        modelClass: CheckIn
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./check-in.js").default>>} */
  loadCheckIns() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["survey_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "check_ins",
        model: this,
        modelClass: CheckIn
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./check-in.js").default>>} */
  ensureCheckInsLoaded() {
    return this.ensureAssociationLoaded("check_ins")
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./survey-step.js").default>} */
  steps() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["survey_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "steps",
        model: this,
        modelClass: SurveyStep
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./survey-step.js").default>>} */
  loadSteps() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["survey_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "steps",
        model: this,
        modelClass: SurveyStep
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./survey-step.js").default>>} */
  ensureStepsLoaded() {
    return this.ensureAssociationLoaded("steps")
  }
}
